<template>
  <div class="floor-plan-wrapper">
    <!-- Loading state -->
    <div v-if="loading" class="fp-loading">
      <div class="fp-spinner"></div>
    </div>

    <!-- Canvas (once layout is ready) -->
    <FloorPlanCanvas
      v-else-if="resolvedLayout"
      :layout="resolvedLayout"
      v-model="internalSelection"
      :tableStatuses="tableStatuses"
      :sectionStatuses="sectionStatuses"
      :tableCapacities="tableCapacities"
      :interactive="interactive"
      :bookingDropEnabled="bookingDropEnabled"
      :showLegend="showLegend"
      :showModeToggle="showModeToggle"
      :statusColours="statusColours"
      :poolTablePricePerHour="resolvedPoolPrice"
      :locale="locale" :currency="currency"
      @table-click="emit('table-click', $event)"
      @table-drop="onTableDrop"
      @section-click="emit('section-click', $event)"
      @pool-table-click="emit('pool-table-click', $event)"
      @update:modelValue="onSelectionUpdate"
    />

    <div v-else class="fp-no-venue">
      <p>Select a venue to view the floor plan</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { apiBaseUrl } from '../runtime'
import { ref, watch } from 'vue'
import FloorPlanCanvas from './FloorPlanCanvas.vue'
import { resolveVenueLayout } from '../layout'
import type {
  FloorPlanLayout,
  FloorPlanSelection,
  TableBookingStatus,
  SectionBookingStatus,
} from '../types'

const props = withDefaults(defineProps<{
  venueId?: string
  modelValue?: FloorPlanSelection
  tableStatuses?: TableBookingStatus[]
  sectionStatuses?: SectionBookingStatus[]
  tableCapacities?: Record<number, number>
  interactive?: boolean
  // Enables staff booking cards to be dropped onto table targets without
  // enabling the customer-facing table selection controls.
  bookingDropEnabled?: boolean
  showLegend?: boolean
  showModeToggle?: boolean
  // Colour free tables as Available rather than by section (staff service view)
  statusColours?: boolean
  // Allow a pre-loaded layout to be passed in (editor uses this)
  preloadedLayout?: FloorPlanLayout | null
  // API URL so the wrapper can fetch the custom layout
  apiUrl?: string
  // Explicit price override — if provided, skips internal fetch
  poolTablePricePerHour?: number
  locale?: string
  currency?: string
}>(), {
  interactive: true,
  showLegend: true,
  showModeToggle: true,
  statusColours: false,
  tableStatuses: () => [],
  sectionStatuses: () => [],
  tableCapacities: () => ({}),
  bookingDropEnabled: false,
  apiUrl: apiBaseUrl(),
  poolTablePricePerHour: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [sel: FloorPlanSelection]
  'table-click': [tableId: number]
  'table-drop': [tableId: number, bookingId: string]
  'section-click': [sectionId: string]
  'pool-table-click': [poolTableId: string]
}>()

const loading = ref(false)
const resolvedLayout = ref<FloorPlanLayout | null>(null)
// Resolved pool table price — prop takes priority, otherwise fetched from API
const resolvedPoolPrice = ref<number | undefined>(props.poolTablePricePerHour)
let loadVersion = 0

const internalSelection = ref<FloorPlanSelection>(
  props.modelValue || {
    mode: 'table', selectedTableIds: [], selectedSectionId: null,
    entireVenue: false, selectedPoolTableId: null, poolTableHours: 1,
  }
)

watch(() => props.modelValue, (v) => {
  if (v) internalSelection.value = v
})

function onSelectionUpdate(sel: FloorPlanSelection) {
  internalSelection.value = sel
  emit('update:modelValue', sel)
}

function onTableDrop(tableId: number, bookingId: string) {
  emit('table-drop', tableId, bookingId)
}

// Load layout: preloaded → custom from API → default
async function loadLayout() {
  const version = ++loadVersion
  const venueId = props.venueId
  const apiUrl = props.apiUrl
  const priceOverride = props.poolTablePricePerHour
  const isCurrent = () => version === loadVersion && venueId === props.venueId && apiUrl === props.apiUrl
  resolvedLayout.value = null
  resolvedPoolPrice.value = priceOverride
  loading.value = false

  if (!venueId) return

  // If a pre-loaded layout was passed in (e.g. from editor), use it directly
  if (props.preloadedLayout) {
    resolvedLayout.value = props.preloadedLayout
    return
  }

  loading.value = true
  try {
    // Fetch layout and pool table price in parallel
    const [venueRes, priceRes] = await Promise.all([
      fetch(`${apiUrl}/venues/${venueId}`),
      priceOverride === undefined
        ? fetch(`${apiUrl}/venues/${venueId}/pool-table-config`).catch(() => null)
        : Promise.resolve(null),
    ])
    if (!isCurrent()) return

    // Apply pool table price from API (only if not provided as a prop)
    if (priceRes && priceRes.ok) {
      const priceData = await priceRes.json()
      if (!isCurrent()) return
      if (typeof priceData.pricePerHour === 'number' && Number.isFinite(priceData.pricePerHour)) {
        resolvedPoolPrice.value = priceData.pricePerHour
      }
    }

    if (venueRes.ok) {
      const data = await venueRes.json()
      if (!isCurrent()) return
      // Saved layout, else JB & Sons' built-in plan (only where the venue has one), else a generated one.
      resolvedLayout.value = resolveVenueLayout(venueId, data)
      return
    }
    resolvedLayout.value = null
  } catch {
    if (!isCurrent()) return
    // Network error or API down: nothing to draw (never another venue's plan).
    resolvedLayout.value = null
  } finally {
    if (isCurrent()) loading.value = false
  }
}

watch(
  [() => props.venueId, () => props.apiUrl, () => props.poolTablePricePerHour, () => props.preloadedLayout],
  loadLayout,
  { immediate: true },
)
</script>

<style scoped>
.floor-plan-wrapper {
  width: 100%;
}

.fp-loading {
  display: flex;
  justify-content: center;
  padding: 60px;
}

.fp-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid color-mix(in srgb, var(--jb-color-brand) 12%, transparent);
  border-top-color: var(--jb-color-brand);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.fp-no-venue {
  padding: 60px 20px;
  text-align: center;
  color: color-mix(in srgb, var(--jb-color-brand) 50%, transparent);
  background: color-mix(in srgb, var(--jb-color-brand) 3%, transparent);
  border-radius: 10px;
  border: 2px dashed color-mix(in srgb, var(--jb-color-brand) 15%, transparent);
  font-family: var(--font-body);
}
</style>
